import room1Image from '../../assets/images/room1.jpg';
import room2Image from '../../assets/images/room2.jpg';
// Mock data for buildings and rooms - SAT V4 Zone
import type { Building, Room, RoomType } from '../../types';

export const mockBuildings: Building[] = [
  { id: 'bldg-main', name: 'Main Building', code: 'MB', floors: 3, isAccessible: true },
  { id: 'bldg-science', name: 'Science Block', code: 'SB', floors: 2, isAccessible: false },
  { id: 'bldg-arts', name: 'Art Department Building', code: 'AC', floors: 2, isAccessible: true },
  { id: 'bldg-sports', name: 'Games Building', code: 'SC', floors: 1, isAccessible: true },
  { id: 'bldg-tech', name: 'Technology Wing', code: 'TW', floors: 2, isAccessible: false },
];

// Room images array
const roomImages = [room1Image, room2Image];

function generateRoomAvailability() {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const periods = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
  const availability: Record<string, Record<string, 'available' | 'booked' | 'maintenance'>> = {};

  for (const day of days) {
    availability[day] = {};
    for (const period of periods) {
      const rand = Math.random();
      availability[day][period] = rand > 0.95 ? 'maintenance' : rand > 0.6 ? 'booked' : 'available';
    }
  }
  return availability;
}

export const mockRooms: Room[] = [
  // Mix of different buildings for variety in preview
  {
    id: 'room-sb-1',
    name: 'Chemistry lab 1',
    code: 'SB01',
    buildingId: 'bldg-science',
    floor: 1,
    capacity: 24,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'OHP', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-ac-3',
    name: 'Music Room 1',
    code: 'AC03',
    buildingId: 'bldg-arts',
    floor: 2,
    capacity: 30,
    roomType: 'Music Room',
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-sc-1',
    name: 'Main Gymnasium',
    code: 'SC01',
    buildingId: 'bldg-sports',
    floor: 1,
    capacity: 100,
    roomType: 'Games Room',
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-tw-1',
    name: 'Computer Lab 1',
    code: 'TW01',
    buildingId: 'bldg-tech',
    floor: 1,
    capacity: 30,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-mb-1',
    name: 'Room 101',
    code: 'MB01',
    buildingId: 'bldg-main',
    floor: 1,
    capacity: 30,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-sb-6',
    name: 'Room 206',
    code: 'SB06',
    buildingId: 'bldg-science',
    floor: 2,
    capacity: 28,
    roomType: 'Staff Room' as RoomType,
    equipment: ['Whiteboard', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-ac-1',
    name: 'Art Studio 1',
    code: 'AC01',
    buildingId: 'bldg-arts',
    floor: 1,
    capacity: 25,
    roomType: 'Classroom',
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  // Main Building - Additional Classrooms
  {
    id: 'room-mb-2',
    name: 'Room 102',
    code: 'MB02',
    buildingId: 'bldg-main',
    floor: 1,
    capacity: 32,
    roomType: 'Classroom' as RoomType,
    equipment: ['Whiteboard', 'Chalkboard'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-mb-3',
    name: 'Room 103',
    code: 'MB03',
    buildingId: 'bldg-main',
    floor: 1,
    capacity: 28,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'OHP'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-mb-4',
    name: 'Room 201',
    code: 'MB04',
    buildingId: 'bldg-main',
    floor: 2,
    capacity: 35,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-mb-5',
    name: 'Room 202',
    code: 'MB05',
    buildingId: 'bldg-main',
    floor: 2,
    capacity: 30,
    roomType: 'Staff Room' as RoomType,
    equipment: ['Whiteboard', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-mb-6',
    name: 'Room 203',
    code: 'MB06',
    buildingId: 'bldg-main',
    floor: 2,
    capacity: 32,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  // Science Block - Additional Labs
  {
    id: 'room-sb-2',
    name: 'Chemistry lab 2',
    code: 'SB02',
    buildingId: 'bldg-science',
    floor: 1,
    capacity: 24,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'Chalkboard'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-sb-3',
    name: 'Physics lab',
    code: 'SB03',
    buildingId: 'bldg-science',
    floor: 1,
    capacity: 26,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'OHP', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-sb-4',
    name: 'Biology lab',
    code: 'SB04',
    buildingId: 'bldg-science',
    floor: 2,
    capacity: 24,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-sb-5',
    name: 'Room 304',
    code: 'SB05',
    buildingId: 'bldg-science',
    floor: 3,
    capacity: 30,
    roomType: 'Classroom' as RoomType,
    equipment: ['Whiteboard', 'Chalkboard', 'OHP'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-sb-7',
    name: 'Room 105',
    code: 'SB07',
    buildingId: 'bldg-science',
    floor: 1,
    capacity: 25,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Whiteboard'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  // Art Department Building - Additional Rooms
  {
    id: 'room-ac-2',
    name: 'Art Studio 2',
    code: 'AC02',
    buildingId: 'bldg-arts',
    floor: 1,
    capacity: 22,
    roomType: 'Classroom',
    equipment: ['Whiteboard', 'OHP', 'Chalkboard'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-ac-4',
    name: 'Music Room 2',
    code: 'AC04',
    buildingId: 'bldg-arts',
    floor: 2,
    capacity: 28,
    roomType: 'Music Room',
    equipment: ['Whiteboard', 'OHP', 'Laptops/Tablet'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-ac-5',
    name: 'Drama Studio',
    code: 'AC05',
    buildingId: 'bldg-arts',
    floor: 1,
    capacity: 35,
    roomType: 'Classroom',
    equipment: ['Projector', 'Whiteboard'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  // Games Building - Additional Rooms
  {
    id: 'room-sc-2',
    name: 'Sports Hall',
    code: 'SC02',
    buildingId: 'bldg-sports',
    floor: 1,
    capacity: 150,
    roomType: 'Games Room',
    equipment: ['Whiteboard', 'OHP'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-sc-3',
    name: 'Fitness Room',
    code: 'SC03',
    buildingId: 'bldg-sports',
    floor: 1,
    capacity: 40,
    roomType: 'Games Room',
    equipment: ['Projector', 'Whiteboard'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-sc-4',
    name: 'Room G01',
    code: 'SC04',
    buildingId: 'bldg-sports',
    floor: 1,
    capacity: 30,
    roomType: 'Classroom',
    equipment: ['Whiteboard', 'Chalkboard', 'OHP'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  // Technology Wing - Additional Labs
  {
    id: 'room-tw-2',
    name: 'Computer Lab 2',
    code: 'TW02',
    buildingId: 'bldg-tech',
    floor: 1,
    capacity: 28,
    roomType: 'Classroom' as RoomType,
    equipment: ['Projector', 'Laptops/Tablet', 'OHP'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-tw-3',
    name: 'IT Lab',
    code: 'TW03',
    buildingId: 'bldg-tech',
    floor: 1,
    capacity: 32,
    roomType: 'Classroom' as RoomType,
    equipment: ['Whiteboard', 'Laptops/Tablet', 'Chalkboard'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available' as const,
    images: roomImages,
  },
  {
    id: 'room-tw-4',
    name: 'Design & Technology Workshop',
    code: 'TW04',
    buildingId: 'bldg-tech',
    floor: 2,
    capacity: 20,
    roomType: 'Classroom',
    equipment: ['Projector', 'Whiteboard', 'OHP'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-tw-5',
    name: 'Food Technology Room',
    code: 'TW05',
    buildingId: 'bldg-tech',
    floor: 2,
    capacity: 20,
    roomType: 'Classroom',
    equipment: ['Whiteboard', 'OHP', 'Chalkboard'],
    isAccessible: false,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-tw-6',
    name: 'Room T01',
    code: 'TW06',
    buildingId: 'bldg-tech',
    floor: 1,
    capacity: 25,
    roomType: 'Staff Room',
    equipment: ['Laptops/Tablet', 'Whiteboard'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  // Library - Main Building
  {
    id: 'room-lib',
    name: 'Library',
    code: 'LIB',
    buildingId: 'bldg-main',
    floor: 1,
    capacity: 60,
    roomType: 'Classroom',
    equipment: ['Projector', 'Whiteboard', 'Laptops/Tablet'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
  {
    id: 'room-mb-staff',
    name: 'Staff Room',
    code: 'MB-SR',
    buildingId: 'bldg-main',
    floor: 1,
    capacity: 40,
    roomType: 'Staff Room',
    equipment: ['Whiteboard', 'Laptops/Tablet', 'Projector'],
    isAccessible: true,
    availability: generateRoomAvailability(),
    status: 'available',
    images: roomImages,
  },
];

export default mockRooms;
